import type { RideRecord } from "../types/ride";

export type RideRecordValidationIssue = {
  code: string;
  message: string;
  index: number;
};

function getMedian(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 1) {
    return sorted[middle];
  }

  return (sorted[middle - 1] + sorted[middle]) / 2;
}

export function validateRideRecords(records: RideRecord[]): RideRecordValidationIssue[] {
  const issues: RideRecordValidationIssue[] = [];

  if (records.length === 0) {
    issues.push({
      code: "no-records",
      message: "No ride records extracted.",
      index: -1
    });
    return issues;
  }

  const hasNormalizedTimestamp = records.some(record => {
    if (!record.timestamp) {
      return false;
    }

    const timestamp = Date.parse(record.timestamp);
    return !Number.isNaN(timestamp);
  });

  if (!hasNormalizedTimestamp) {
    issues.push({
      code: "timestamp-unavailable",
      message: "No record timestamps could be normalized.",
      index: -1
    });
  }

  for (let index = 1; index < records.length; index += 1) {
    const previousRecord = records[index - 1];
    const currentRecord = records[index];

    if (previousRecord.timestamp && currentRecord.timestamp) {
      const previousTimestamp = Date.parse(previousRecord.timestamp);
      const currentTimestamp = Date.parse(currentRecord.timestamp);

      if (!Number.isNaN(previousTimestamp) && !Number.isNaN(currentTimestamp) && currentTimestamp < previousTimestamp) {
        issues.push({
          code: "chronological-order",
          message: "Record timestamps are not chronological.",
          index
        });
      }
    }

    const previousElapsedSeconds = previousRecord.elapsedSeconds ?? null;
    const currentElapsedSeconds = currentRecord.elapsedSeconds ?? null;

    if (previousElapsedSeconds !== null && currentElapsedSeconds !== null && currentElapsedSeconds < previousElapsedSeconds) {
      issues.push({
        code: "elapsed-seconds",
        message: "Elapsed seconds decreased between consecutive records.",
        index
      });
    }

    if (currentRecord.powerWatts !== null && currentRecord.powerWatts !== undefined && currentRecord.powerWatts < 0) {
      issues.push({
        code: "power-negative",
        message: "Power values must be non-negative.",
        index
      });
    }

    if (currentRecord.heartRateBpm !== null && currentRecord.heartRateBpm !== undefined && currentRecord.heartRateBpm < 0) {
      issues.push({
        code: "heart-rate-negative",
        message: "Heart rate values must be non-negative.",
        index
      });
    }

    if (currentRecord.cadenceRpm !== null && currentRecord.cadenceRpm !== undefined && currentRecord.cadenceRpm < 0) {
      issues.push({
        code: "cadence-negative",
        message: "Cadence values must be non-negative.",
        index
      });
    }
  }

  return issues;
}

export function inferRecordingIntervalSeconds(records: RideRecord[]): number | null {
  if (records.length < 2) {
    return null;
  }

  const timestampIntervals: number[] = [];
  const elapsedIntervals: number[] = [];

  for (let index = 1; index < records.length; index += 1) {
    const previousRecord = records[index - 1];
    const currentRecord = records[index];

    if (previousRecord.timestamp && currentRecord.timestamp) {
      const previousTimestamp = Date.parse(previousRecord.timestamp);
      const currentTimestamp = Date.parse(currentRecord.timestamp);

      if (!Number.isNaN(previousTimestamp) && !Number.isNaN(currentTimestamp)) {
        const timestampIntervalSeconds = (currentTimestamp - previousTimestamp) / 1000;
        if (timestampIntervalSeconds > 0) {
          timestampIntervals.push(timestampIntervalSeconds);
        }
      }
    }

    const previousElapsedSeconds = previousRecord.elapsedSeconds ?? null;
    const currentElapsedSeconds = currentRecord.elapsedSeconds ?? null;

    if (previousElapsedSeconds !== null && currentElapsedSeconds !== null) {
      const elapsedIntervalSeconds = currentElapsedSeconds - previousElapsedSeconds;
      if (elapsedIntervalSeconds > 0) {
        elapsedIntervals.push(elapsedIntervalSeconds);
      }
    }
  }

  const intervals = timestampIntervals.length > 0 ? timestampIntervals : elapsedIntervals;
  if (intervals.length === 0) {
    return null;
  }

  const medianInterval = getMedian(intervals);
  if (medianInterval === null || medianInterval <= 0) {
    return null;
  }

  const q1 = getMedian(intervals.slice(0, Math.floor(intervals.length / 2))) ?? medianInterval;
  const upperHalfStart = Math.ceil(intervals.length / 2);
  const q3 = getMedian(intervals.slice(upperHalfStart)) ?? medianInterval;
  const interquartileRange = Math.max(0, q3 - q1);
  const upperFence = q3 + interquartileRange * 3;

  const filteredIntervals = intervals.filter(interval => interval > 0 && interval <= upperFence);
  const filteredMedian = getMedian(filteredIntervals);
  if (filteredMedian === null || filteredMedian <= 0) {
    return null;
  }

  return Math.round(filteredMedian * 1000) / 1000;
}
